/*
 * Author: Ben Zhao
 * File Created: Tuesday, 25-06-17 10:10 am
 * Last Modified: Friday, 25-06-27 03:47 pm, by Dw
 */

import { useEffect, useMemo } from 'react';

import { useRouter } from 'next/router';

import { create } from 'zustand';

import { isOpenApi } from '@ucard/next-core/utils';

import { showStatusDialog } from '@ucard/ds/message';

import { createStoreActions, generateUseShallow } from '@web/utils';

import useLanguage from '../hooks/useLanguage';
import { getNotifications } from '../services/notification';
import { useUserInfo } from './useUserStore';

interface Notification {
  id: string;
  title?: string;
  content?: string;
  bizType: 'GLOBAL' | 'DVAULT_HOME';
  type: 'MODAL' | 'DVAULT_BANNER';
  btn: string;
}

interface IState {
  notifications: Notification[];
  isFetching: boolean;
}

const defaultState: IState = {
  notifications: [],
  isFetching: true,
};

interface IAction {
  fetchNotifications: (_p: {
    locale: string;
    bizType?: string;
  }) => Promise<void>;
}

const NOTIFICATION_STORE_KEY = 'dnks';

const actions = createStoreActions<IState, IAction>((set) => ({
  fetchNotifications: async (params) => {
    set({ isFetching: true });
    try {
      const { data } = await getNotifications(params);

      set({ notifications: data, isFetching: false });
    } catch (err) {
      console.error('fetchNotifications failed', err);

      set({ isFetching: false });
    }
  },
}));

const useNotificationsStore = create<IAction & IState>((set, get) => ({
  ...defaultState,
  ...actions(set, get),
}));

export const useNotifications = generateUseShallow<IState, IAction>(
  useNotificationsStore,
);

export const useNotificationsFilter = (params?: {
  bizType?: 'GLOBAL' | 'DVAULT_HOME';
  type?: 'MODAL' | 'DVAULT_BANNER';
}) => {
  const { notifications } = useNotifications();

  const ns = useMemo(() => {
    if (!params?.bizType && !params?.type) {
      return notifications;
    }

    let tmp = notifications;

    if (params.bizType) {
      tmp = tmp.filter((item) => item.bizType === params.bizType);
    }

    if (params.type) {
      tmp = tmp.filter((item) => item.type === params.type);
    }

    return tmp;
  }, [params?.bizType, params?.type, notifications]);

  return ns;
};

export const useNotificationFetch = (bizType: string) => {
  const { isFetching } = useNotifications(['isFetching']);

  const showModals = async () => {
    const { notifications } = useNotificationsStore.getState();

    const readBannerKeyArrStr = localStorage.getItem(NOTIFICATION_STORE_KEY);

    let arr: string[] = [];

    try {
      arr = JSON.parse(readBannerKeyArrStr || '[]') || [];
    } catch (e) {
      arr = [];
    }

    const modals = notifications.filter(
      (item) =>
        item.bizType === bizType &&
        item.type === 'MODAL' &&
        !arr.includes(item.id),
    );

    if (!modals.length) {
      return true;
    }

    for (const item of modals) {
      // eslint-disable-next-line no-await-in-loop -- need
      await new Promise((resolve) => {
        const close = showStatusDialog({
          header: item.title,
          content: item.content,
          btn1: {
            label: item.btn,
            onClick: () => {
              const arrStr = localStorage.getItem(NOTIFICATION_STORE_KEY);

              let tmpArr: string[] = [];

              try {
                tmpArr = JSON.parse(arrStr || '[]') || [];
              } catch (e) {
                tmpArr = [];
              }

              tmpArr.push(item.id);

              localStorage.setItem(
                NOTIFICATION_STORE_KEY,
                JSON.stringify(tmpArr),
              );
              resolve(true);
              close();
            },
          },
          onClose: () => {
            resolve(true);
          },
        });
      });
    }
  };

  return {
    loading: isFetching,
    fn: showModals,
  };
};

export const useNotificationEstablish = () => {
  const { fetchNotifications } = useNotifications(['fetchNotifications']);

  const { isLogin } = useUserInfo();
  const { curLocale } = useLanguage();
  const router = useRouter();

  useEffect(() => {
    // 不能是 openAPI
    if (!isOpenApi && router.pathname !== '/landing/home' && isLogin) {
      fetchNotifications({
        locale: curLocale,
      }).catch((err) => {
        console.error('fetchNotifications failed', err);
      });
    } else if (isLogin === false) {
      useNotificationsStore.setState({ isFetching: false });
    }
  }, [isLogin, curLocale, router.pathname]);
};
